/* eslint-disable no-nested-ternary */
import { CboRole, FeatureFlag } from '@cbo/shared-library';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { MutableRefObject, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { LABOR_RULE_EDIT_VIEW_TAB_KEY } from '../LaborRuleEditView/LaborRuleEditView';
import Header from '../../components/Header/Header';
import LaborRuleCard from '../LaborRuleCard/LaborRuleCard';
import RuleGuidanceDrawer from '../RuleGuidanceDrawer/RuleGuidanceDrawer';
import {
  LaborRuleApplicabilityData,
  LaborRuleConfigData,
  LaborRuleGroup,
  MinorRuleTypes,
  RuleTypes,
  SchedulingRuleTypes,
  ruleTypesByGroup,
} from '../models/LaborRuleConfiguration';
import { JurisdictionSitesRow } from '../JurisdictionSitesDataGrid/JurisdictionSitesDataGrid';
import { filterRuleConfigsForType, getFilteredRuleGroups, mutateConfigsForRuleForm } from '../utils/utils';
import { SingleSelectGroup } from '../../components/GlobalFilterBar/types';
import SingleSelect from '../../components/GlobalFilterBar/SingleSelect/SingleSelect';
import { JurisdictionResponse } from '../models/JurisdictionModels';
import { useUsers } from '../../contexts/userContext';
import isPermitted from '../../lib/permissions';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';

export const RULE_GROUPS = [
  LaborRuleGroup.OVERTIME,
  LaborRuleGroup.BREAKS,
  LaborRuleGroup.WAGES_AND_TIPS,
  LaborRuleGroup.SCHEDULING,
  LaborRuleGroup.MINORS,
];

export const renderTabs = (tabNames: LaborRuleGroup[] | string[], t: TFunction<'translation', undefined>) =>
  tabNames.map((tabName) => (
    <Tab key={tabName} label={tabName && t(`laborRules.tabs.${tabName}`)} data-testid={`${tabName}-tab`} />
  ));

const parseJurisdiction = (jurisdiction: string) => {
  const parts = jurisdiction.split('.');
  const mostSpecific = parts[parts.length - 1];
  return mostSpecific === 'Flagstaff' ? mostSpecific : parts[1]?.replaceAll('_', ' ') || '';
};

const getRuleConfigsByGroup = (
  ruleConfigs: LaborRuleConfigData[] | undefined,
  currentTabName: LaborRuleGroup,
  currentJurisdictionPath: string
) =>
  ruleConfigs
    ? (mutateConfigsForRuleForm(
        ruleConfigs.filter((ruleConfig) => ruleConfig.rule?.group === currentTabName)
      ) as LaborRuleConfigData[])
    : [];

const getDefaultRuleConfigsByGroup = (
  defaultRuleConfigs: LaborRuleConfigData[] | undefined,
  currentTabName: LaborRuleGroup,
  currentJurisdictionPath: string
) =>
  defaultRuleConfigs
    ? mutateConfigsForRuleForm(
        defaultRuleConfigs.filter(
          (defaultRuleConfig) =>
            defaultRuleConfig.rule?.group === currentTabName && currentJurisdictionPath.includes(defaultRuleConfig.path)
        )
      )
    : [];

export interface LaborRulesFilter {
  jurisdictions: SingleSelectGroup[];
}

export interface LaborRulesMainViewProps {
  setLruaModalOpen: (value: boolean) => void;
  selectedJurisdiction: string;
  setSelectedJurisdiction: (value: string) => void;
  jurisdictionPath: string;
  setJurisdictionPath: (value: string) => void;
  defaultRuleConfigs: LaborRuleConfigData[] | undefined;
  defaultRuleConfigsLoading: boolean;
  orgRuleConfigsLoading: boolean;
  lruaRecordLoading: boolean;
  orgRuleConfigs: LaborRuleConfigData[] | undefined;
  sites: JurisdictionSitesRow[] | undefined;
  refetchConfigs: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<LaborRuleConfigData[], unknown>>;
  jurisdictionData: JurisdictionResponse | void;
  refetchJurisdictionData: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<void | JurisdictionResponse, unknown>>;
  ruleApplicabilities: LaborRuleApplicabilityData[] | undefined;
  furthestAwayWorkWeekDate: string;
  lastSelectedJurisdictionRef: MutableRefObject<string | undefined>;
}

export default function LaborRulesMainView(props: LaborRulesMainViewProps) {
  const {
    setLruaModalOpen,
    jurisdictionPath,
    setJurisdictionPath,
    selectedJurisdiction,
    setSelectedJurisdiction,
    defaultRuleConfigs,
    defaultRuleConfigsLoading,
    orgRuleConfigs,
    orgRuleConfigsLoading,
    lruaRecordLoading,
    sites: unfilteredSites,
    refetchConfigs,
    jurisdictionData,
    refetchJurisdictionData,
    ruleApplicabilities,
    furthestAwayWorkWeekDate,
    lastSelectedJurisdictionRef,
  } = props;

  const { t } = useTranslation();
  const cboLaborRulesEnabled = useFeatureFlag(FeatureFlag.laborRules);
  const cboLaborRulesTipsWages = useFeatureFlag(FeatureFlag.laborRulesTipsWages);
  const isInitialMount = useRef(true);
  // As more tabs need to be revealed, add them to the filteredRuleGroups array
  // Once all tabs are revealed, remove the filteredRuleGroups and use RULE_GROUPS
  const filteredRuleGroups = getFilteredRuleGroups(cboLaborRulesEnabled, cboLaborRulesTipsWages, RULE_GROUPS);

  const [tabIndex, setTabIndex] = useState<number>(
    JSON.parse(localStorage.getItem(LABOR_RULE_EDIT_VIEW_TAB_KEY) || '0')
  );
  const theme = useTheme();
  const handleTabChange = (event: SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };

  const { state: locationState } = useLocation();
  // Only run once, clears localStorage once tabIndex has value
  useEffect(() => {
    if (localStorage.getItem(LABOR_RULE_EDIT_VIEW_TAB_KEY) !== null) {
      localStorage.removeItem(LABOR_RULE_EDIT_VIEW_TAB_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentTabName = filteredRuleGroups[tabIndex] || filteredRuleGroups[0];

  const ruleConfigsByGroup = getRuleConfigsByGroup(orgRuleConfigs, currentTabName, jurisdictionPath);

  const defaultRuleConfigsByGroup = getDefaultRuleConfigsByGroup(defaultRuleConfigs, currentTabName, jurisdictionPath);
  const user = useUsers();
  const isUserLREWriter = isPermitted(user, [CboRole.LRE_RULE_WRITER]);

  const filteredJurisdictions = useMemo(() => {
    if (!jurisdictionData?.jurisdictions) return [];
    return (
      isUserLREWriter
        ? jurisdictionData.jurisdictions
        : jurisdictionData.jurisdictions.filter((juris) => juris.siteEuIds.length > 0)
    ).sort((a, b) => {
      const aSegments = a.path.split('.');
      const bSegments = b.path.split('.');

      // Compare by the second segment (state level) first
      const aSeg = aSegments[1] || '';
      const bSeg = bSegments[1] || '';
      const stateComparison = aSeg.localeCompare(bSeg);

      if (stateComparison !== 0) return stateComparison;

      // If states are the same, compare by depth and then by the deepest level
      if (aSegments.length !== bSegments.length) {
        return aSegments.length - bSegments.length;
      }

      // Same depth, compare by the deepest level
      const aDeepest = aSegments[aSegments.length - 1] || '';
      const bDeepest = bSegments[bSegments.length - 1] || '';
      return aDeepest.localeCompare(bDeepest);
    });
  }, [isUserLREWriter, jurisdictionData?.jurisdictions]);

  const sites = useMemo(() => {
    if (!unfilteredSites || !filteredJurisdictions) return [];

    const uniqueSiteIds = new Set();
    return unfilteredSites.filter((site) => {
      const isInJurisdiction = filteredJurisdictions.some((juris) => juris.siteEuIds.includes(site.id));
      if (isInJurisdiction && !uniqueSiteIds.has(site.id)) {
        uniqueSiteIds.add(site.id);
        return true;
      }
      return false;
    });
  }, [unfilteredSites, filteredJurisdictions]);

  const firstMySiteIndex =
    !filteredJurisdictions || filteredJurisdictions.length === 0
      ? 0
      : (() => {
          const index = filteredJurisdictions.findIndex(
            (juris) => juris.path.includes('.') && juris.siteEuIds.length > 0
          );
          if (filteredJurisdictions[index]?.path === 'United_States_of_America' && filteredJurisdictions.length > 1) {
            return index + 1;
          }
          return index !== -1 ? index : 0;
        })();

  const formContext = useForm<LaborRulesFilter & { showCityList: boolean }>({
    defaultValues: {
      jurisdictions:
        filteredJurisdictions.length > 0
          ? [
              {
                label: filteredJurisdictions[firstMySiteIndex]?.path.split('.')[1],
                jurisdictionData: filteredJurisdictions[firstMySiteIndex],
              },
            ]
          : [],
    },
  });

  const { reset } = formContext;
  const { getValues } = formContext;

  const jurisdictionFormValues: SingleSelectGroup[] = getValues('jurisdictions');

  // Reset the mount flag when data changes or on navigation
  useEffect(() => {
    isInitialMount.current = true;
  }, [jurisdictionData, filteredJurisdictions, firstMySiteIndex, locationState]);

  useEffect(() => {
    let initialJurisdiction;

    // Initialization logic (mount/reset/navigation)
    const isJurisdictionDataExists =
      jurisdictionData && Array.isArray(jurisdictionData.jurisdictions) && jurisdictionData.jurisdictions.length > 0;
    const hasJurisdictions = isUserLREWriter
      ? isJurisdictionDataExists
      : Array.isArray(filteredJurisdictions) && filteredJurisdictions.length > 0;

    if (isInitialMount.current && hasJurisdictions) {
      // Use jurisdictionData for LRE writers
      if (isUserLREWriter && hasJurisdictions) {
        initialJurisdiction = jurisdictionData?.jurisdictions[firstMySiteIndex];
      }
      // Use filteredJurisdictions for non-LRE writers
      if (!isUserLREWriter && hasJurisdictions) {
        initialJurisdiction = filteredJurisdictions[firstMySiteIndex];
      }
      // Override with ref if present and valid
      if (lastSelectedJurisdictionRef && isJurisdictionDataExists && lastSelectedJurisdictionRef.current) {
        const found = jurisdictionData.jurisdictions.find((j) => j.path === lastSelectedJurisdictionRef.current);
        if (found) {
          initialJurisdiction = found;
        }
      }
      if (initialJurisdiction) {
        const pathParts = initialJurisdiction.path.split('.');
        let labelToSet = pathParts[pathParts.length - 1];
        labelToSet = labelToSet.replaceAll('_', ' ');
        reset({
          jurisdictions: [
            {
              label: labelToSet,
              jurisdictionData: initialJurisdiction,
            },
          ],
        });
        // If restoring a sub-jurisdiction (any level > 2), set a custom field to signal SingleSelect to open nested view
        if (pathParts.length > 2 && formContext.setValue) {
          formContext.setValue('showCityList', true, { shouldDirty: false });
        }
        setJurisdictionPath(initialJurisdiction.path);
        setSelectedJurisdiction(initialJurisdiction.path);
        lastSelectedJurisdictionRef.current = initialJurisdiction.path;
      } else {
        reset({ jurisdictions: [] });
        setJurisdictionPath('');
        setSelectedJurisdiction('');
        lastSelectedJurisdictionRef.current = undefined;
      }
      isInitialMount.current = false;
      return;
    }

    // User selection logic (runs after initialization)
    if (!isInitialMount.current && jurisdictionFormValues.length > 0 && jurisdictionFormValues[0]?.jurisdictionData) {
      const selectedPath = jurisdictionFormValues[0].jurisdictionData?.path;
      setJurisdictionPath(selectedPath);
      setSelectedJurisdiction(selectedPath);
      lastSelectedJurisdictionRef.current = selectedPath;
    }
    // eslint-disable-next-line
  }, [
    filteredJurisdictions,
    isUserLREWriter,
    jurisdictionData,
    firstMySiteIndex,
    reset,
    locationState,
    setJurisdictionPath,
    setSelectedJurisdiction,
    jurisdictionFormValues,
  ]);

  const onViewUserAgreement = () => {
    setLruaModalOpen(true);
  };

  const generateOptions = () => {
    // 1. Build state-level options (unique states, assign tab by siteEuIds, as in generateOptionsOld)
    const stateOptions: SingleSelectGroup[] = [];
    const stateSet = new Set<string>();
    const mySitesStates = new Set<string>();

    // Find all unique states from filteredJurisdictions
    const uniqueStates = [...new Set(filteredJurisdictions?.map((j) => j.path.split('.')[1]))]
      .filter((state) => state !== undefined && state !== 'undefined')
      .sort((a, b) => a.localeCompare(b));

    uniqueStates.forEach((state) => {
      let tabValue = t('labor.other');
      // Find the state-level jurisdiction for this state
      const stateJuris = jurisdictionData?.jurisdictions.find((juris) => {
        const jurisElements = juris.path.split('.');
        if (jurisElements[1] && jurisElements[1] === state && jurisElements.length === 2) {
          if (juris.siteEuIds.length > 0) tabValue = t('labor.mySites');
          return true;
        }
        return false;
      });

      if (stateJuris) {
        if (isUserLREWriter && tabValue === t('labor.mySites')) {
          mySitesStates.add(state);
        }
        const option: SingleSelectGroup = {
          label: state.replaceAll('_', ' '),
          jurisdictionData: stateJuris,
          ...(isUserLREWriter ? { tab: tabValue } : {}),
        };
        stateOptions.push(option);
        stateSet.add(state);
      }
    });

    const options = [...stateOptions];
    return options.sort((a, b) => a.label.localeCompare(b.label));
  };

  return (
    <Box data-testid='labor-rules-main-view'>
      <Grid container alignItems='start' py={2}>
        <Grid
          item
          xs={12}
          sx={{ position: 'sticky', top: '4.5rem', zIndex: 100, backgroundColor: theme.palette.background.default }}
        >
          {isUserLREWriter && (
            <Grid container sx={{ marginBottom: 2 }}>
              {lruaRecordLoading ? (
                <Grid item xs={12}>
                  <Skeleton variant='rounded' height={32} width={400} />
                </Grid>
              ) : (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Header title={t(`laborRules.title`)} dataTestId='labor-rules-header' />
                  <Button
                    data-testid='view-user-agreement-button'
                    onClick={onViewUserAgreement}
                    color='secondary'
                    endIcon={<InsertDriveFileIcon />}
                  >
                    {t('laborRules.viewUserAgreement')}
                  </Button>
                </Grid>
              )}
            </Grid>
          )}

          <Grid item xs={2} sx={{ marginBottom: 2 }}>
            {lruaRecordLoading ? (
              <Skeleton variant='rounded' height={48} width={250} />
            ) : (
              <Box>
                <FormContainer formContext={formContext}>
                  <SingleSelect
                    useTabs={isUserLREWriter}
                    controllerName='jurisdictions'
                    options={generateOptions()}
                    jurisdictionData={jurisdictionData || undefined}
                    titleText={t('laborRules.editRuleView.jurisdictionLabel')}
                    errorHandling={{
                      refetch: refetchJurisdictionData,
                      showError: jurisdictionData
                        ? jurisdictionData.missingSites.length > 0 || jurisdictionData.missingPaths.length > 0
                        : false,
                    }}
                    buttonText='Jurisdiction'
                  />
                </FormContainer>
              </Box>
            )}
          </Grid>
          <Grid item>
            {lruaRecordLoading ? (
              <Stack direction='row' sx={{ marginBottom: 2 }}>
                {filteredRuleGroups.map((name) => (
                  <Skeleton key={`${name}-tab-skeleton`} width={110} height={35} sx={{ marginRight: 1 }} />
                ))}
              </Stack>
            ) : (
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                sx={{ marginBottom: 2 }}
                data-testid='labor-rule-group-tabs'
              >
                {renderTabs(filteredRuleGroups, t)}
              </Tabs>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item sx={{ marginBottom: 2 }}>
            {lruaRecordLoading || defaultRuleConfigsLoading ? (
              <Skeleton variant='rounded' height={90} />
            ) : (
              <RuleGuidanceDrawer
                activeTab={currentTabName}
                jurisdiction={parseJurisdiction(selectedJurisdiction)}
                defaultRuleConfigs={defaultRuleConfigsByGroup}
              />
            )}
          </Grid>
          <Grid item sx={{ marginBottom: 2 }}>
            {((ruleTypesByGroup[currentTabName] as RuleTypes[]) || [])
              .filter(
                (type: RuleTypes) =>
                  ![MinorRuleTypes.SummerVacationRule, MinorRuleTypes.MinorSchoolDaysRule].includes(
                    type as MinorRuleTypes
                  )
              )
              .filter(
                (type: RuleTypes) =>
                  ![
                    SchedulingRuleTypes.AdjustedRateRemainingTimePredictabilityPayRule,
                    SchedulingRuleTypes.ExtraHoursPredictabilityPayRule,
                    SchedulingRuleTypes.FixedFeePredictabilityPayRule,
                  ].includes(type as SchedulingRuleTypes)
              )
              .map((type: RuleTypes) =>
                lruaRecordLoading || orgRuleConfigsLoading ? (
                  <Skeleton key={`${type}-rule-card-skeleton`} variant='rounded' height={300} sx={{ marginTop: 2 }} />
                ) : (
                  <LaborRuleCard
                    key={`${type}-rule-card`}
                    ruleType={type}
                    ruleGroup={currentTabName}
                    title={type && t(`laborRules.rules.${type}.name`)}
                    data-testid={`${type}-rule-card`}
                    ruleConfigs={ruleConfigsByGroup.filter((conf) => filterRuleConfigsForType(conf, type))}
                    sites={sites}
                    jurisdictionPath={jurisdictionPath}
                    refetchConfigs={refetchConfigs}
                    orgRuleConfigsLoading={orgRuleConfigsLoading}
                    defaultRuleConfigs={defaultRuleConfigsByGroup}
                    selectedJurisdiction={selectedJurisdiction}
                    ruleApplicabilities={ruleApplicabilities}
                    furthestAwayWorkWeekDate={furthestAwayWorkWeekDate}
                  />
                )
              )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
